import React, { useEffect } from "react";
import { useGetComponentStringsContext } from "../../hooks/useGetComponentStrings";
import { GetLanguageStrings } from "../../api/languagePickerController";
import {
	List,
	ListItem,
	ListItemText,
	Menu,
	MenuItem,
	Icon,
} from "../../common/MaterialComponent";
import "./LanguagePicker.css";
import Emitter from "../../common/emitter";

interface LanguagePickerProps {
	resetAppLanguage: (data: { components: any }) => void;
}

export const LanguagePicker: React.FC<LanguagePickerProps> = ({
	resetAppLanguage,
}) => {
	const strings = useGetComponentStringsContext().LanguagePicker;
	// TODO: change key value as per json file name
	const options = [
		{ key: "", value: strings.englishOption },
		{ key: "french", value: strings.frenchOption },
		{ key: "spanish", value: strings.spanishOption },
	];
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [selectedIndex, setSelectedIndex] = React.useState(0);
	let previousIndex: number = 0;

	const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuItemClick = (
		event: React.MouseEvent<HTMLElement>,
		index: number
	) => {
		if (selectedIndex === index) {
			return;
		}
		// set previously selected item index
		previousIndex = selectedIndex;
		setSelectedIndex(index);
		setLanguage(options[index].key);
		setAnchorEl(null);
	};

	useEffect(() => {
		// find selected language name from storage and set as selected
		const language = localStorage.getItem("languageName");
		const index = options.findIndex((o) => o.key === language);
		if (index > -1) {
			setLanguage(options[index].key);
			setSelectedIndex(index);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleClose = () => {
		setAnchorEl(null);
	};

	const setLanguageNameInStorage = (language: string) => {
		localStorage.setItem("languageName", language);
	};

	const setLanguage = async (language: string) => {
		if (language) {
			try {
				// if json file for selected language exist in local storage, skip API call and load from storage
				if ((localStorage as any).getItem(language)) {
					resetAppLanguage(JSON.parse((localStorage as any).getItem(language)));
					setLanguageNameInStorage(language);
					return;
				}
				// API call to load json file
				const res = await GetLanguageStrings(language);
				if (res.error) {
					Emitter.emit(
						"showSnackbarMessage",
						strings.languageDetailErrorMessage1 +
							language +
							strings.languageDetailErrorMessage2
					);
					// set previously selected item index
					setSelectedIndex(previousIndex);
				} else {
					if (res.responseObj && res.responseObj.components) {
						resetAppLanguage(res.responseObj);
						// store in local storage of browser
						localStorage.setItem(language, JSON.stringify(res.responseObj));
						setLanguageNameInStorage(language);
						return;
					}
				}
			} catch (err) {
				Emitter.emit(
					"showSnackbarMessage",
					strings.languageDetailErrorMessage1 +
						language +
						strings.languageDetailErrorMessage2
				);
				// set previously selected item
				setSelectedIndex(previousIndex);
			}
		} else {
			// selected option is English
			resetAppLanguage({ components: {} });
			setLanguageNameInStorage(options[0].key);
		}
	};
	return (
		<>
			<List component="nav">
				<ListItem
					button
					className="languageListItem"
					onClick={handleClickListItem}
				>
					<Icon>translate</Icon>
					<ListItemText
						secondary={options[selectedIndex].value}
						className="languageListItemText"
					/>
					<Icon>keyboard_arrow_down</Icon>
				</ListItem>
			</List>
			<Menu
				anchorEl={anchorEl}
				keepMounted
				open={Boolean(anchorEl)}
				onClose={handleClose}
			>
				{options.map((option, index) => (
					<MenuItem
						key={index}
						selected={index === selectedIndex}
						onClick={(event) => handleMenuItemClick(event, index)}
					>
						{option.value}
					</MenuItem>
				))}
			</Menu>
		</>
	);
};
